import type { DeviceRegistrationResult, DeviceTagType } from '~/components/devices/devices'
import type { DeviceLinkedAccessGroup } from '~/types/AccessGroup'
import type { AccountSelection } from '~/types/Account'
import type { DeviceRedirectEntry, AssignedClientsSaveRequest, CacheGroupSettings, Device, DeviceAlarmHistoryData, DeviceBlackListEntry, DeviceCreateRequest, DeviceData, DeviceDirectLinkedClient, DevicelineEntry, DevicePaginationRequest, DeviceRedirectAddress, DeviceSelectionRow, DevicesOverviewPaginationResponse, DeviceType, LinkedDevice, MappingInformation, PaginationRequest, VirtualMappingInformation } from '~/types/Device'

import type { CommandMetrics, DeviceCommandResponse } from '~/types/DeviceControlPanel'
import type { DeviceWithExclusionPaginationRequest } from '~/types/DeviceSelectionView'
import type { DeviceLocation } from '~/types/DevicesMap'
import type { TablePaginationRequest, TablePaginationResponse } from '~/types/GenericTable'
import { api } from '~/utils/backend'

export function paginateStatusOverview(paginationRequest: PaginationRequest) {
  return api.post<DevicesOverviewPaginationResponse>('/devices/paginate/status-overview', paginationRequest)
}

export function paginateDevices(paginationRequest: DevicePaginationRequest) {
  return api.post<TablePaginationResponse<DeviceData>>('/devices/paginate-new', paginationRequest)
}
export function findAvailableTags() {
  return api.get<DeviceTagType[]>('/devices/get/tags')
}

export function paginateHosts(paginationRequest: TablePaginationRequest) {
  return api.post<TablePaginationResponse<string>>('/devices/hosts/paginate', paginationRequest)
}

export function findAlarmHistory(hexacode: string) {
  return api.get<DeviceAlarmHistoryData[]>('/devices/alarm-history', {
    params: {
      hexacode: hexacode,
    },
  })
}

export function clearAlarmHistory(hexacode: string) {
  return api.delete<never>('/devices/clear-alarm-history', {
    params: {
      hexacode: hexacode,
    },
  })
}

export function resetFirstConnectionDate(hexacode: string) {
  return api.put<never>(`/devices/${hexacode}/reset-first-connection`)
}

export function deleteDevice(hexacode: string) {
  return api.delete<never>('/devices/delete', {
    params: {
      hexacode: hexacode,
    },
  })
}

export function updateSimNumber(hexacode: string, simNumber: string) {
  return api.post<never>(`/devices/${hexacode}/simNumber/update`, simNumber)
}

export function findDeviceByHexacode(hexacode: string) {
  return api.get<Device>(`/devices/get/${hexacode}`)
}

export function claimRegisteredDevice(hexacode: string) {
  return api.post<never>(`/devices/${hexacode}/claim`)
}

export function disconnectDevice(hexacode: string) {
  return api.get<never>(`/devices/${hexacode}/disconnect`)
}

export function deleteMapping(mapping: MappingInformation) {
  return api.post<never>('/devices/delete-mapping', mapping)
}

export function findBlacklistedDevices() {
  return api.get<DeviceBlackListEntry[]>('/devices/blacklist')
}

export function findRedirectedDevices() {
  return api.get<DeviceRedirectEntry[]>('/devices/redirected')
}

export function removeRedirectedDevice(hexacode: string) {
  return api.delete<never>('/devices/redirected/remove', {
    params: {
      hexacode: hexacode,
    },
  })
}

export function paginateWithExclusion(paginationRequest: DevicePaginationRequest) {
  return api.post<TablePaginationResponse<DeviceData>>('/devices/paginate/exclude', paginationRequest)
}

export function getDeviceSelection(paginationRequest: TablePaginationRequest) {
  return api.post<TablePaginationResponse<LinkedDevice>>('/devices/paginate/device-selection', paginationRequest)
}

export function downloadFile(exportFilters: unknown) {
  return api.get<never>('/devices/report', { params: exportFilters, responseType: 'blob' })
}

export function findLinkedClientsByHexacode(hexacode: string) {
  return api.get<DeviceDirectLinkedClient[]>(`/devices/${hexacode}/linked-clients`)
}

export function findLinkedAccessGroupsByHexacode(hexacode: string) {
  return api.get<DeviceLinkedAccessGroup[]>(`/devices/${hexacode}/access-groups`)
}

export function findWebClientsByHexacode(hexacode: string) {
  return api.get<AccountSelection[]>(`/devices/${hexacode}/web-clients`)
}

export function saveDevicePermissions(request: AssignedClientsSaveRequest) {
  return api.post<number>(`/devices/${request.hexacode}/permissions/save`, request.permissions)
}

export function getDeviceTimeline(hexacode: string) {
  return api.get<DevicelineEntry[]>(`/devices/${hexacode}/connection-graph`, {
    params: {
      startDate: null,
      endDate: null,
    },
  })
}

export function sendCommand(command: FormData) {
  return api.post<DeviceCommandResponse[]>('/devices/sendCommand', command, { headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export function findDeviceResponseChartData(hexacode: string) {
  return api.get<CommandMetrics[]>(`/devices/metrics/${hexacode}`)
}

export function findDeviceSelectionByType(types: DeviceType[]) {
  return api.post<DeviceSelectionRow[]>('/devices/get/device-selection-by-type', types)
}

export function findRealDeviceDepartments(hexacode: string) {
  return api.get<VirtualMappingInformation[]>('/departments/real/' + hexacode)
}

export function validateHexacode(id: string | null, hexacode: string) {
  return api.get<boolean>('/devices/validate/hexacode', {
    params: {
      id: id,
      hexacode: hexacode,
    },
  })
}
export function getDefaultCacheSettings() {
  return api.get<CacheGroupSettings[]>('/devices/cache/get')
}

export function getDeviceAvailableRedirectAddresses() {
  return api.get<DeviceRedirectAddress[]>('/devices/redirect-addresses')
}

export function saveDevice(device: DeviceCreateRequest) {
  return api.post<never>('/devices/save', device)
}

export function updateDevice(device: DeviceCreateRequest) {
  return api.put<never>('/devices/update', device)
}
export function getDevicesLocation(paginationRequest: DevicePaginationRequest) {
  return api.post<DeviceLocation[]>('/devices/devices-location', paginationRequest)
}
export function paginateOptimaBoxWithExclusion(paginationRequest: DeviceWithExclusionPaginationRequest) {
  return api.post<TablePaginationResponse<DeviceData>>('/devices/optimabox/paginate/exclude', paginationRequest)
}
export function bulkDeviceRegistration(formData: FormData) {
  return api.post<DeviceRegistrationResult[]>('/devices/registration/bulk', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

export function paginateCustomers(request: TablePaginationRequest) {
  return api.post<TablePaginationResponse<string>>('/devices/customers/paginate', request)
}

export function triggerManualTimeSyncronization(hexacode: string) {
  return api.post<never>('/devices/time-sync', null, {
    params: {
      hexacode: hexacode,
    },
  })
}
